import React from "react";
import { ReactComponent as IconInformation } from "@assets/Icons/Iconinformation.svg";
import { ReactComponent as IconSuccess } from "@assets/Icons/Icon_Success.svg";
import { ReactComponent as IconWarning } from "@assets/Icons/Icon_Warning.svg";

export type InlineAlertVariant = "error" | "warning" | "success" | "info";

export interface GenericInlineAlertProps {
    variant?: InlineAlertVariant;
    /** Optional bold heading rendered above the message. */
    title?: string;
    /** Message body — plain text or JSX. */
    children: React.ReactNode;
    /** Spacing/layout classes for the call site. Colours come from the variant. */
    className?: string;
}

type VariantStyle = {
    bg: string;
    border: string;
    text: string;
    Icon: React.FC<React.SVGProps<SVGSVGElement>>;
    /** "alert" interrupts the screen reader; "status" waits for a pause. */
    role: "alert" | "status";
};

/**
 * The design system has no `error` colour family — the red one is named
 * `danger`. Classes like `text-semantic-text-error` are therefore not
 * generated by Tailwind at all and silently render unstyled, which is the
 * defect this component was extracted to fix (CTX-170). Only reference token
 * names that exist in `tailwind.config.js`.
 */
const VARIANT_STYLES: Record<InlineAlertVariant, VariantStyle> = {
    error: {
        bg: "bg-semantic-bg-danger-secondary",
        border: "border-semantic-border-danger-secondary",
        text: "text-semantic-text-danger",
        Icon: IconWarning,
        role: "alert",
    },
    warning: {
        bg: "bg-semantic-bg-warning-secondary",
        border: "border-semantic-border-warning-secondary",
        text: "text-semantic-text-warning",
        Icon: IconWarning,
        role: "alert",
    },
    success: {
        bg: "bg-semantic-bg-success-secondary",
        border: "border-semantic-border-success-secondary",
        text: "text-semantic-text-success",
        Icon: IconSuccess,
        role: "status",
    },
    info: {
        bg: "bg-semantic-bg-brand-primary",
        border: "border-semantic-border-brand-secondary",
        text: "text-semantic-text-brand",
        Icon: IconInformation,
        role: "status",
    },
};

/**
 * Inline status banner for messages that live *in* the page or modal flow:
 * form validation, blocking states, contextual notices.
 *
 * For a blocking confirm/acknowledge step use `GenericAlert` (or the
 * `useAlerts` hook) instead — that one portals a dialog over the viewport.
 */
const GenericInlineAlert: React.FC<GenericInlineAlertProps> = ({
    variant = "error",
    title,
    children,
    className = "",
}) => {
    const { bg, border, text, Icon, role } = VARIANT_STYLES[variant];

    return (
        <div
            role={role}
            className={`w-full flex items-start gap-2 rounded-sm border-l-4 p-4 text-sm ${bg} ${border} ${text} ${className}`}
        >
            <Icon className="w-5 h-5 mt-0.5 shrink-0" aria-hidden="true" />
            <div className="min-w-0">
                {title && <div className="font-semibold">{title}</div>}
                <div className={title ? "mt-1" : undefined}>{children}</div>
            </div>
        </div>
    );
};

export default GenericInlineAlert;
